import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth, estAdmin } from '../lib/auth'
import { poids } from '../lib/format'
import type { Chantier, DocumentChantier } from '../lib/types'
import { PlanOuvert } from './PlanOuvert'
import { messageClair, televerserGros } from '../lib/televersement'
import { base } from '../lib/db'

const FAMILLES: { cle: DocumentChantier['type']; titre: string }[] = [
  { cle: 'architecture', titre: 'Architecture' },
  { cle: 'structure', titre: 'Structure' },
  { cle: 'soumission', titre: 'Soumission' },
  { cle: 'etendue', titre: 'Étendue des travaux' },
  { cle: 'autre', titre: 'Autres documents' }
]

export function Plans({ chantier }: { chantier: Chantier }) {
  const { profil } = useAuth()
  const [documents, setDocuments] = useState<DocumentChantier[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [ouvert, setOuvert] = useState<DocumentChantier | null>(null)
  const [importEnCours, setImportEnCours] = useState(false)
  const [avance, setAvance] = useState(0)

  const charger = useCallback(async () => {
    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('chantier_id', chantier.id)
      .in('type', ['architecture', 'structure', 'soumission', 'etendue', 'autre'])
      .order('nom')
    if (error) setErreur(error.message)
    else setDocuments((data as DocumentChantier[]) ?? [])
  }, [chantier.id])

  useEffect(() => {
    void charger()
  }, [charger])

  async function importer(fichier: File | undefined) {
    if (!fichier) return
    setImportEnCours(true)
    setErreur(null)
    try {
      const chemin = `${chantier.id}/${crypto.randomUUID()}.pdf`
      setAvance(0)
      if (fichier.size > 6 * 1024 * 1024) {
        // Gros plan : envoi par morceaux, avec reprise si la connexion coupe.
        await televerserGros('documents', chemin, fichier, setAvance)
      } else {
        const { error: e1 } = await supabase.storage
          .from('documents')
          .upload(chemin, fichier, { contentType: 'application/pdf' })
        if (e1) throw e1
      }
      // Copie locale du fichier : l'ouverture qui suit (découpage et lecture
      // des cartouches) n'a pas à le retélécharger.
      try {
        await base.plans.put({ cle: chemin, octets: await fichier.arrayBuffer(), conserve_le: Date.now() })
      } catch {
        // espace insuffisant : il sera retéléchargé
      }
      const { data: cree, error: e2 } = await supabase.from('documents').insert({
        chantier_id: chantier.id,
        type: 'architecture',
        nom: fichier.name.replace(/\.pdf$/i, ''),
        fichier_path: chemin,
        taille_octets: fichier.size,
        importe_par: profil?.id ?? null
      })
        .select()
        .single()
      if (e2) throw e2
      await charger()
      // Ouverture immédiate : la préparation pour les téléphones et la
      // lecture des cartouches démarrent toutes seules.
      if (cree) setOuvert(cree as DocumentChantier)
    } catch (e) {
      setErreur(messageClair(e))
    } finally {
      setImportEnCours(false)
    }
  }

  if (ouvert) {
    return (
      <PlanOuvert
        document={ouvert}
        fermer={() => {
          setOuvert(null)
          void charger()
        }}
      />
    )
  }

  return (
    <>
      {erreur && <p className="avis erreur" style={{ marginBottom: 12 }}>{erreur}</p>}

      {documents === null && <p className="vide">Chargement…</p>}

      {documents !== null && documents.length === 0 && (
        <p className="vide">
          Aucun plan importé.
          {estAdmin(profil) ? (
            <>
              <br />
              Les plans s'importent depuis un ordinateur : les fichiers font souvent plusieurs
              dizaines de mégaoctets.
            </>
          ) : (
            <>
              <br />
              Un administrateur doit importer les plans du chantier.
            </>
          )}
        </p>
      )}

      {FAMILLES.map((f) => {
        const liste = (documents ?? []).filter((d) => d.type === f.cle)
        if (liste.length === 0) return null
        return (
          <div key={f.cle}>
            <div className="sect">
              {f.titre} <span className="code">{liste.length}</span>
            </div>
            <div className="liste">
              {liste.map((d) => (
                <button key={d.id} className="carte bande-nulle" onClick={() => setOuvert(d)}>
                  <div className="entre">
                    <div>
                      <p className="titre">{d.nom}</p>
                    </div>
                    {d.numero_revision && (
                      <span className="pastille p-ouvert">{d.numero_revision}</span>
                    )}
                  </div>
                  <p className="meta">
                    <span>{poids(d.taille_octets)}</span>
                    {d.nb_pages ? (
                      <>
                        <span className="pt" />
                        <span>{d.nb_pages} pages</span>
                      </>
                    ) : null}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )
      })}

      {estAdmin(profil) && (
        <label className="bouton creux" style={{ marginTop: 20, cursor: 'pointer' }}>
          {importEnCours
            ? `Import en cours… ${Math.round(avance * 100)} %`
            : 'Importer un plan PDF'}
          <input
            type="file"
            accept="application/pdf"
            hidden
            disabled={importEnCours}
            onChange={(e) => void importer(e.target.files?.[0])}
          />
        </label>
      )}
    </>
  )
}
